'use client'

import { useState, SetStateAction, useEffect } from "react"
// my imports:
import Hint, { HintProps, HintLink } from "@/app/components/Hint"

const defaultStartLinkText: string = 'Start→'
const defaultNextLinkText: string = 'Next→';
const defaultFinishedLinkText: string = 'OK!';
const defaultSkipLinkText: string = 'Skip'


export type HintManagerProps = {
    allEntries: HintManagerEntry[],
    startLinkText?: string,
    nextLinkText?: string,
    finishedLinkText?: string,
    skipLinkText?: string,
    useSkip?: boolean,
    skipOnStartOnly?: boolean,
    loopHints?: boolean,
    setFinished?: React.Dispatch<SetStateAction<boolean>>
}

export type HintManagerEntry = {
    text: string,
    position?: {x: number, y: number},
    element?: Element,
    elementOffset?: {x: number, y: number}
}

export default function HintManager({
    allEntries,
    startLinkText,
    nextLinkText,
    finishedLinkText,
    skipLinkText,
    useSkip,
    skipOnStartOnly,
    loopHints,
    setFinished
} : HintManagerProps) {

    const [currentHint, setCurrentHint] = useState<number>(0);

    // if allHints ever changes, we reset to 0
    useEffect(() => {
        setCurrentHint(0); 
    }, [allEntries]);

    // if we try to click past the last hint, we're done
    useEffect(() => {
        if (currentHint >= allEntries.length) {
            setFinished?.(true);
            if (loopHints) {
                setCurrentHint(0);
            }
        }
    }, [currentHint])

    if (currentHint >= allEntries.length)
        return null;

    function incrementHint() {
        setCurrentHint(prev => prev + 1);
    }
    if (!startLinkText)
        startLinkText = defaultStartLinkText;
    if (!nextLinkText)
        nextLinkText = defaultNextLinkText;
    if (!finishedLinkText)
        finishedLinkText = defaultFinishedLinkText;
    if (!skipLinkText)
        skipLinkText = defaultSkipLinkText;

    const allHintProps: HintProps[] = [];
    // we're gonna build the hint props from the manager entries
    for (const [index, entry] of allEntries.entries()) {
        // easy readability for index we might be on
        let lastEntry = false;
        let firstEntry = false;
        if (index === allEntries.length - 1)
            lastEntry = true;
        else if (index === 0)
            firstEntry = true;

        // first we need to set up the links
        const hintLinks: HintLink[] = [];
        // if we have skip on, we push a setFinished link to the links array
        // but only if it's not the last
        if (
            useSkip && !lastEntry
            && ((skipOnStartOnly && firstEntry) || !skipOnStartOnly)
        ) {
            // if we only skip on start, then we need it to be the first entry
            hintLinks.push({
                text: skipLinkText,
                callback: () => setFinished?.(true)
            })
        };
        let text: string = '';
        if (firstEntry)
            text = startLinkText;
        else if (lastEntry)
            text = finishedLinkText;
        else
            text = nextLinkText;

        // we always push the next link, but the text depends on last entry
        hintLinks.push({
            text: text,
            callback: incrementHint
        });
        // now we push to whole entry itself to the array of allHintProps
        const hintProps: HintProps = {
            position: entry.position,
            element: entry.element,
            elementOffset: entry.elementOffset,
            text: entry.text,
            links: hintLinks
        }
        allHintProps.push(hintProps);
    }

    return (
        <Hint 
            key={currentHint}
            position={allHintProps[currentHint].position}
            element={allHintProps[currentHint].element}
            elementOffset={allHintProps[currentHint].elementOffset}
            text={allHintProps[currentHint].text}
            links={allHintProps[currentHint].links}
        />
    )
}